import { OrderTypes } from "@/lib/supabase/enums";

import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "motion/react";

import { getSessionId } from "@/lib/session-cache";

import DeliveryIcon from "./icons/delivery-icon";
import TakeAwayIcon from "./icons/take-away-icon";
import { BookOpenIcon, MenuIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCartStore } from "./cart/store";
import { useLocaleNavigation } from "@/hooks/use-locale-navigation";
import { useCustomerStore } from "./customer/store";

import { checkDeliveryArea } from "@/lib/delivery-areas";
import I18NLink from "./i18n-link";
import OpeningHoursList from "./opening-hours-list";
import {
  Availability,
  ClosedMessage,
  openingHoursQueryOptions,
} from "@/lib/shifts";
import { useQuery } from "@tanstack/react-query";
import { useGetTranslatedOrderType } from "@/hooks/use-get-translated-order-type";
import {
  useHasDeliveryShifts,
  useEnsureValidOrderType,
} from "@/hooks/use-has-delivery-shifts";
import { DeliveryArea } from "@/lib/delivery-areas";
import { cn, formatNumberToCurrency } from "@/lib/utils";
import Decimal from "decimal.js";

/**
 * Shows the note a merchant left when closing an online order shift, in place of
 * (or alongside) the generic closed text. Labelled per order type when only some
 * of them are closed, unlabelled when the whole day carries the same note.
 */
function ClosedMessageNotice({
  label,
  message,
}: {
  label?: string;
  message: string;
}) {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-left dark:border-amber-900/40 dark:bg-amber-950/30">
      {label && (
        <p className="mb-1 text-sm font-semibold text-amber-900 dark:text-amber-100">
          {label}
        </p>
      )}
      <p className="text-sm leading-relaxed whitespace-pre-line text-amber-800 dark:text-amber-200">
        {message}
      </p>
    </div>
  );
}

export default function InitOrder({
  availability,
  closedMessages,
}: {
  availability: Availability[];
  /** Notes the merchant left when closing an order type for today, if any. */
  closedMessages: ClosedMessage[];
}) {
  //global state
  const customer = useCustomerStore((status) => status.customer);
  const setCustomer = useCustomerStore((status) => status.setCustomer);
  const setOrderType = useCartStore((state) => state.setOrderType);

  //local state
  const [showDeliveryForm, setShowDeliveryForm] = useState(false);
  const [deliveryArea, setDeliveryArea] = useState<DeliveryArea | null>(null);
  const [postalCode, setPostalCode] = useState(customer.zipCode);
  const [houseNumber, setHouseNumber] = useState(customer.streetNumber);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [validationSuccess, setValidationSuccess] = useState(false);
  const [showFloatingBack, setShowFloatingBack] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  //queries
  const { data: openingHoursData } = useQuery(openingHoursQueryOptions());
  const openingHours = openingHoursData?.data ?? {};

  const isOpen = availability.filter((a) => a.spots > 0).length > 0;

  // Closing the whole day leaves the same note on every order type, so collapse
  // it into one notice rather than repeating it per order type.
  const uniqueClosedMessages = Array.from(
    new Set(closedMessages.map((entry) => entry.message))
  );
  const sharedClosedMessage =
    uniqueClosedMessages.length === 1 ? uniqueClosedMessages[0] : null;

  // Whether the business offers delivery at all (has delivery shifts). When it
  // does not, the delivery option is hidden and the cart defaults to pickup.
  const hasDeliveryShifts = useHasDeliveryShifts();
  useEnsureValidOrderType();

  const isDeliveryAvailable =
    availability.filter(
      (availability) =>
        availability.type === OrderTypes.delivery && availability.spots > 0
    ).length > 0;

  const isPickupAvailable =
    availability.filter(
      (availability) =>
        availability.type === OrderTypes.pickup && availability.spots > 0
    ).length > 0;

  //translations
  const { t } = useTranslation("translation");

  //hooks
  const { navigateWithLocale } = useLocaleNavigation();
  const { getTranslatedOrderType } = useGetTranslatedOrderType();

  // Rendered both above the order options (when only some order types are
  // closed) and in place of the generic closed text (when nothing is orderable).
  const closedMessageNotices =
    closedMessages.length === 0 ? null : sharedClosedMessage ? (
      <ClosedMessageNotice message={sharedClosedMessage} />
    ) : (
      closedMessages.map((entry) => (
        <ClosedMessageNotice
          key={entry.orderType}
          label={getTranslatedOrderType(entry.orderType)}
          message={entry.message}
        />
      ))
    );

  //handlers
  // Measure and set content height
  const measureHeight = useCallback(() => {
    const activeRef = showDeliveryForm ? formRef : optionsRef;
    if (activeRef.current) {
      const height = activeRef.current.scrollHeight;
      setContentHeight(height);
    }
  }, [showDeliveryForm]);

  const validateDelivery = useCallback(async () => {
    if (!postalCode.trim() || !houseNumber.trim()) {
      setValidationError("Vul beide velden in");
      return;
    }

    setIsValidating(true);
    setValidationError("");

    try {
      // Generate consistent session ID for this address combination
      const sessionId = getSessionId(postalCode, houseNumber);
      const result = await checkDeliveryArea({
        data: {
          sessionId,
          zipCode: postalCode,
          streetNumber: houseNumber,
        },
      });
      if (!result.address) {
        setDeliveryArea(null);
        setValidationSuccess(false);
        switch (result.code) {
          case "address_not_in_delivery_area":
            setValidationError(
              t("InitOrder.Errors.address_not_in_delivery_area")
            );
            break;
          case "delivery_area_unavailable_today":
            setValidationError(
              t("InitOrder.Errors.delivery_area_unavailable_today")
            );
            break;
          case "address_not_valid":
            setValidationError(t("InitOrder.Errors.address_not_valid"));
            break;
          default:
            setValidationError(t("InitOrder.Errors.general_error"));
            break;
        }
      } else {
        setDeliveryArea(result.deliveryArea);
        setValidationSuccess(true);
        setValidationError("");
        // Save validated values to store
        setCustomer({
          ...customer,
          streetName: result.address.street,
          streetNumber: houseNumber,
          zipCode: postalCode,
          city: result.address.city,
        });
        setOrderType(OrderTypes.delivery);
      }

      // Here you could redirect to the main ordering flow
    } catch {
      setValidationError(
        "Adres niet gevonden. Controleer je postcode en huisnummer."
      );
      setValidationSuccess(false);
    } finally {
      setIsValidating(false);
    }
  }, [postalCode, houseNumber, setOrderType]);

  const handleDeliveryClick = () => {
    // Measure current height before transition
    if (optionsRef.current) {
      setContentHeight(optionsRef.current.scrollHeight);
    }
    setOrderType(OrderTypes.delivery);

    setIsTransitioning(true);

    setTimeout(() => {
      setShowDeliveryForm(true);
      setTimeout(() => {
        measureHeight();
        setTimeout(() => setIsTransitioning(false), 250);
      }, 50);
    }, 250);
  };

  const handlePickupClick = () => {
    setOrderType(OrderTypes.pickup);
    navigateWithLocale("/menu");
  };

  const handleBackToOptions = useCallback(() => {
    // Measure current height before transition
    if (formRef.current) {
      setContentHeight(formRef.current.scrollHeight);
    }

    setIsTransitioning(true);

    setTimeout(() => {
      setShowDeliveryForm(false);
      setPostalCode("");
      setHouseNumber("");
      setValidationError("");
      setValidationSuccess(false);
      setTimeout(() => {
        measureHeight();
        setTimeout(() => setIsTransitioning(false), 250);
      }, 50);
    }, 250);
  }, [measureHeight]);

  // Sync form values when store is hydrated
  useEffect(() => {
    setPostalCode(customer.zipCode);
    setHouseNumber(customer.streetNumber);
  }, [customer]);

  // Effect to measure height when content changes
  useEffect(() => {
    const timer = setTimeout(() => {
      measureHeight();
    }, 50); // Small delay to ensure DOM is updated

    return () => clearTimeout(timer);
  }, [showDeliveryForm, measureHeight, validationError, validationSuccess]);

  // Re-measure on window resize and element size changes
  useEffect(() => {
    const activeRef = showDeliveryForm ? formRef : optionsRef;
    const node = activeRef.current;
    if (!node) return;

    // Initial measurement
    measureHeight();

    // Observe element size changes
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => {
        measureHeight();
      });
      ro.observe(node);
      resizeObserverRef.current = ro;
    }

    // Fallback: handle window resize/orientation
    const handleResize = () => {
      measureHeight();
    };
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [showDeliveryForm, measureHeight]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (showDeliveryForm && e.key === "Escape") {
        handleBackToOptions();
      }
      if (
        showDeliveryForm &&
        e.key === "Enter" &&
        postalCode.trim() &&
        houseNumber.trim() &&
        !isValidating
      ) {
        validateDelivery();
      }
    };

    const handleScroll = () => {
      setShowFloatingBack(window.scrollY > 100);
    };

    if (showDeliveryForm) {
      document.addEventListener("keydown", handleKeyPress);
      window.addEventListener("scroll", handleScroll);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [
    showDeliveryForm,
    postalCode,
    houseNumber,
    isValidating,
    validateDelivery,
    handleBackToOptions,
  ]);

  return (
    <div className="relative flex flex-col w-full max-w-4xl mx-auto py-20">
      {/* Header with clean rounded design */}

      {isOpen ? (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -30 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="bg-background rounded-2xl p-8 shadow-lg border border-border overflow-hidden"
        >
          {closedMessageNotices && (
            <div className="mb-6 flex flex-col gap-3">
              {closedMessageNotices}
            </div>
          )}
          <div
            ref={contentRef}
            className="transition-all duration-500 ease-in-out"
            style={{
              height: contentHeight ? `${contentHeight}px` : "auto",
              opacity: isTransitioning ? 0.5 : 1,
              transform: isTransitioning ? "scale(0.98)" : "scale(1)",
            }}
          >
            {!showDeliveryForm ? (
              <div
                ref={optionsRef}
                className="grid grid-cols-1 md:grid-cols-2 gap-6 p-2"
              >
                {hasDeliveryShifts && (
                  <button
                    disabled={!isDeliveryAvailable}
                    onClick={handleDeliveryClick}
                    className="not:disabled:group relative overflow-hidden bg-gradient-to-br disabled:opacity-50 disabled:cursor-not-allowed from-blue-50 to-blue-100 dark:from-blue-950/30 dark:to-blue-900/20 rounded-2xl border border-blue-200 dark:border-blue-800/30 not-disabled:hover:border-blue-300 dark:not-disabled:hover:border-blue-700/50 transition-all duration-300 not-disabled:hover:shadow-xl not-disabled:hover:scale-[1.01] cursor-pointer"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative p-8 text-center">
                      <div className="mb-6 flex justify-center">
                        <div className="p-4 bg-blue-500/10 rounded-full group-hover:bg-blue-500/20 transition-colors duration-300">
                          <DeliveryIcon className="w-12 h-12 text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform duration-300" />
                        </div>
                      </div>
                      <h3 className="text-2xl font-bold mb-3 text-blue-900 dark:text-blue-100">
                        {t("OrderTypes.delivery")}
                      </h3>
                      <div className="flex items-center justify-center space-x-2 text-sm text-blue-600 dark:text-blue-400">
                        <span className="font-medium">
                          {t("InitOrder.click_to_check_delivery_area")}→
                        </span>
                      </div>
                    </div>
                  </button>
                )}

                {/* Pickup Option */}
                <button
                  disabled={!isPickupAvailable}
                  onClick={handlePickupClick}
                  className={cn(
                    "group relative overflow-hidden bg-gradient-to-br from-green-50 disabled:opacity-50 disabled:cursor-not-allowed to-green-100 dark:from-green-950/30 dark:to-green-900/20 rounded-2xl border border-green-200 dark:border-green-800/30 hover:border-green-300 dark:hover:border-green-700/50 transition-all duration-300 hover:shadow-xl hover:scale-[1.01] cursor-pointer",
                    !hasDeliveryShifts && "md:col-span-2"
                  )}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative p-8 text-center">
                    <div className="mb-6 flex justify-center">
                      <div className="p-4 bg-green-500/10 rounded-full group-hover:bg-green-500/20 transition-colors duration-300">
                        <TakeAwayIcon className="w-12 h-12 text-green-600 dark:text-green-400 group-hover:scale-110 transition-transform duration-300" />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold mb-3 text-green-900 dark:text-green-100">
                      {t("OrderTypes.pickup")}
                    </h3>
                    {/* <p className="text-green-700 dark:text-green-300 mb-6 leading-relaxed">
                      Haal je bestelling zelf op en bespaar op bezorgkosten.
                      Sneller en goedkoper!
                    </p> */}
                    {/* <div className="flex items-center justify-center space-x-2 text-sm text-green-600 dark:text-green-400">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="font-medium">
                        Klaar in 15-20 minuten
                      </span>
                    </div> */}
                  </div>
                </button>

                {/* View Menu Button */}
                <button
                  onClick={() => navigateWithLocale("/menu")}
                  className="col-span-1 md:col-span-2 group relative overflow-hidden bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/30 dark:to-purple-900/20 rounded-2xl border border-purple-200 dark:border-purple-800/30 hover:border-purple-300 dark:hover:border-purple-700/50 transition-all duration-300 hover:shadow-xl hover:scale-[1.01] cursor-pointer"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative p-6 text-center">
                    <div className="mb-4 flex justify-center">
                      <div className="p-3 bg-purple-500/10 rounded-full group-hover:bg-purple-500/20 transition-colors duration-300">
                        <BookOpenIcon className="w-8 h-8 text-purple-600 dark:text-purple-400 group-hover:scale-110 transition-transform duration-300" />
                      </div>
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-purple-900 dark:text-purple-100">
                      {t("InitOrder.view_menu")}
                    </h3>
                    <p className="text-sm text-purple-700 dark:text-purple-300">
                      {t("InitOrder.view_menu_description")}
                    </p>
                  </div>
                </button>
              </div>
            ) : (
              /* Delivery Form */
              <div
                ref={formRef}
                className="max-w-md mx-auto animate-in slide-in-from-right duration-500"
              >
                <div className="space-y-6">
                  {/* Breadcrumb Navigation */}
                  <div className="flex items-center justify-between mb-6 p-4 bg-muted/30 rounded-xl">
                    <button
                      onClick={handleBackToOptions}
                      className="group flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-all duration-200"
                    >
                      <div className="p-1 rounded-full group-hover:bg-muted transition-colors duration-200">
                        <svg
                          className="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-0.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                      </div>
                      <span>{t("InitOrder.order_method")}</span>
                    </button>

                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground">
                        {t("InitOrder.delivery_area")}
                      </span>
                      <div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
                      <span className="text-muted-foreground">
                        {t("InitOrder.order")}
                      </span>
                    </div>
                  </div>

                  <div className="text-center mb-8">
                    <div className="mx-auto w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors duration-300">
                      <DeliveryIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">
                      {t("InitOrder.delivery_area_validation_title")}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {t("InitOrder.delivery_area_validation_description")}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="postalCode"
                        className="block text-sm font-medium mb-2"
                      >
                        {t("AddressConfirmation.zipcode")}
                      </label>
                      <input
                        id="postalCode"
                        type="text"
                        value={postalCode}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          setPostalCode(value);
                          setCustomer({
                            ...customer,
                            zipCode: value,
                          });
                        }}
                        placeholder="1234AB"
                        className="w-full px-4 py-3 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        maxLength={7}
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="houseNumber"
                        className="block text-sm font-medium mb-2"
                      >
                        {t("AddressConfirmation.streetNumber")}
                      </label>
                      <input
                        id="houseNumber"
                        type="text"
                        value={houseNumber}
                        onChange={(e) => {
                          const value = e.target.value;
                          setHouseNumber(value);
                          setCustomer({
                            ...customer,
                            streetNumber: value,
                          });
                        }}
                        placeholder="123"
                        className="w-full px-4 py-3 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      />
                    </div>

                    {validationError && (
                      <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                        <p className="text-red-600 dark:text-red-400 text-sm">
                          {validationError}
                        </p>
                      </div>
                    )}

                    {validationError && (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground text-center">
                          {t("InitOrder.takeaway_still_available")}
                        </p>
                        <I18NLink
                          to="/menu"
                          className="w-full flex items-center justify-center bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3 px-6 rounded-xl transition-all duration-200 text-sm"
                        >
                          {t("InitOrder.continue_to_menu")} →
                        </I18NLink>
                      </div>
                    )}

                    {deliveryArea && (
                      <div className="bg-green-50 flex flex-col dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-xl p-4 space-y-3">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                            <svg
                              className="w-3 h-3 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                          <p className="text-green-600 dark:text-green-400 text-sm font-medium">
                            {t("InitOrder.delivery_area_validation_success")}
                          </p>
                        </div>
                        <div className="space-y-2 pt-2 border-t border-green-200 dark:border-green-800">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-green-700 dark:text-green-300">
                              {t(
                                "InitOrder.delivery_area_info.minimum_order_amount"
                              )}
                              :
                            </span>
                            <span className="text-sm font-semibold text-green-800 dark:text-green-200">
                              {formatNumberToCurrency({
                                number: new Decimal(
                                  deliveryArea.minOrderAmount ?? 0
                                )
                                  .div(100)
                                  .toNumber(),
                              })}
                            </span>
                          </div>

                          {deliveryArea.fee !== undefined && (
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-green-700 dark:text-green-300">
                                {t("InitOrder.delivery_area_info.delivery_fee")}
                                :
                              </span>
                              <span className="text-sm font-semibold text-green-800 dark:text-green-200">
                                {deliveryArea.fee === 0
                                  ? t("Cart.delivery_fee_free")
                                  : formatNumberToCurrency({
                                      number: deliveryArea.fee,
                                    })}
                              </span>
                            </div>
                          )}
                          {deliveryArea.freeDeliveryFrom && (
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-green-700 dark:text-green-300">
                                {t(
                                  "InitOrder.delivery_area_info.free_delivery_from"
                                )}
                                :
                              </span>
                              <span className="text-sm font-semibold text-green-800 dark:text-green-200">
                                {formatNumberToCurrency({
                                  number: deliveryArea.freeDeliveryFrom,
                                })}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="border-t border-green-200 dark:border-green-800">
                          <I18NLink
                            to="/menu"
                            className="w-full flex items-center justify-center bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 text-sm mt-3"
                          >
                            {t("InitOrder.continue_to_menu")} →
                          </I18NLink>
                        </div>
                      </div>
                    )}

                    <button
                      onClick={validateDelivery}
                      disabled={
                        isValidating ||
                        !postalCode.trim() ||
                        !houseNumber.trim()
                      }
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
                    >
                      {isValidating ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          {t("InitOrder.checking_delivery_area")}...
                        </>
                      ) : (
                        t("InitOrder.check_delivery_area")
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      ) : (
        /* Closed state */
        <div className="bg-background rounded-2xl p-12 shadow-elevate border border-border/50">
          <div className="text-center max-w-md mx-auto">
            <div className="mb-8 flex justify-center">
              <div className="p-6 bg-red-500/10 rounded-full">
                <svg
                  className="w-16 h-16 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <h2 className="text-2xl font-bold mb-4 text-foreground">
              {t("InitOrder.closed_message")}
            </h2>
            {closedMessageNotices ? (
              <div className="mb-8 flex flex-col gap-3">
                {closedMessageNotices}
              </div>
            ) : (
              <p className="text-muted-foreground mb-8 leading-relaxed">
                {t("InitOrder.closed_message_description")}
              </p>
            )}
            <I18NLink
              to="/menu"
              className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3 px-6 rounded-xl transition-all duration-200 mb-8"
            >
              <BookOpenIcon className="w-5 h-5" />
              {t("InitOrder.view_menu")}
            </I18NLink>
            <div className="bg-muted/50 rounded-xl p-6 border border-border/30">
              <h3 className="font-semibold mb-3 text-foreground">
                {t("InitOrder.opening_hours_title")}
              </h3>
              <OpeningHoursList openingHoursData={openingHours} />
            </div>
          </div>
        </div>
      )}

      {/* Floating Back Button */}
      {showDeliveryForm && showFloatingBack && (
        <button
          onClick={handleBackToOptions}
          className="fixed bottom-6 left-6 z-50 p-3 bg-primary text-primary-foreground rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group"
          aria-label="Terug naar opties"
        >
          <svg
            className="w-5 h-5 transition-transform duration-200 group-hover:-translate-x-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
